import { MAP_SPOT_COLORS } from '@gettactic/components/themes/mapSpotColors';
import { getCssVariableValue } from '@gettactic/helpers/src/css/getCssVariableValue';
import {
  SpotAppearance,
  SpotLabelOrientation,
  SpotLabelPlacement,
  SpotOrientation
} from '../../../../types';
import {
  DEFAULT_NODE_CIRCLE_RADIUS,
  DEFAULT_NODE_CIRCLE_STROKE_WIDTH,
  SPOT_WALK_UP_DASH
} from '../../defines';
import { SpotLayout } from '../../types';

export const getOrientationLayout = (
  spotType: SpotAppearance,
  baseOrientation: SpotOrientation,
  labelPlacement: SpotLabelPlacement,
  labelOrientation: SpotLabelOrientation,
  rowIndex: number,
  itemIndex: number,
  spacing: number,
  rowSpacing: number,
  // Actual configured size (e.g. a grid tool cell) for spot types whose rect
  // isn't a fixed constant. Falls back to the desk-sized default when unset.
  nodeWidth?: number,
  nodeHeight?: number
): SpotLayout => {
  const orientation =
    rowIndex % 2 === 0
      ? baseOrientation
      : baseOrientation === 'top'
        ? 'bottom'
        : baseOrientation === 'bottom'
          ? 'top'
          : baseOrientation === 'left'
            ? 'right'
            : 'left';

  const isRoom = spotType === 'meeting_room';
  const isRoundRoom = spotType === 'circle';
  const isParking = spotType === 'parking_lot' || spotType === 'parking_space';
  const isSimpleNode = spotType === 'workspace';

  const RECT_WIDTH = 90;
  const RECT_HEIGHT = 50;

  const baseLayouts: SpotLayout = {
    rect: {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      rotation: 0
    },
    circle: { x: 0, y: 0 },
    text: { x: 0, y: 0, rotation: 0, width: 0, height: 0 }
  };

  const isHorizontal =
    baseOrientation === 'left' || baseOrientation === 'right';
  const isAutoOrientation =
    isHorizontal && (!labelOrientation || labelOrientation === 'auto');
  const isLabelOnBottom = !isAutoOrientation && labelOrientation === 'bottom';
  const isLabelOnTop = !isAutoOrientation && labelOrientation === 'top';

  if (spotType === 'desk') {
    switch (orientation) {
      case 'top':
        baseLayouts.rect = {
          x: 0,
          y: 40,
          width: RECT_WIDTH,
          height: RECT_HEIGHT,
          rotation: 0
        };
        baseLayouts.circle = { x: RECT_WIDTH / 2, y: 32 };
        baseLayouts.text = { x: 0, y: 70, rotation: 0 };

        if (labelPlacement === 'outside') {
          if (isRoom) {
            baseLayouts.text = { x: 0, y: 0, rotation: 0 };
          } else {
            baseLayouts.text = { x: 0, y: 100, rotation: 0 };
          }
        } else if (labelPlacement === 'outside-node') {
          if (isRoom) {
            baseLayouts.text = { x: 0, y: 0, rotation: 0 };
          } else {
            baseLayouts.text = { x: 0, y: -25, rotation: 0 };
          }
        } else if (isRoom) {
          baseLayouts.text = { x: 0, y: 5, rotation: 0 };
        }
        break;
      case 'bottom':
        baseLayouts.rect = {
          x: 0,
          y: 10,
          width: RECT_WIDTH,
          height: RECT_HEIGHT,
          rotation: 0
        };
        baseLayouts.circle = { x: RECT_WIDTH / 2, y: 68 };
        baseLayouts.text = { x: 0, y: 20, rotation: 0 };
        if (labelPlacement === 'outside') {
          if (isRoom) {
            baseLayouts.text = { x: 0, y: 0, rotation: 0 };
          } else {
            baseLayouts.text = { x: 0, y: -12, rotation: 0 };
          }
        } else if (labelPlacement === 'outside-node') {
          if (isRoom) {
            baseLayouts.text = { x: 0, y: 0, rotation: 0 };
          } else {
            baseLayouts.text = { x: 0, y: 105, rotation: 0 };
          }
        } else if (isRoom) {
          baseLayouts.text = { x: 0, y: 95, rotation: 0 };
        }
        break;
      case 'left': {
        const textRotation = isAutoOrientation ? 0 : 0;
        const textWidth = !isAutoOrientation
          ? RECT_HEIGHT - 3
          : RECT_HEIGHT - 26;

        baseLayouts.rect = {
          x: 90,
          y: 0,
          width: RECT_WIDTH,
          height: RECT_HEIGHT,
          rotation: 90
        };
        baseLayouts.circle = { x: 32, y: 45 };
        baseLayouts.text = {
          x: isAutoOrientation ? 65 : 41,
          y: isLabelOnBottom
            ? RECT_WIDTH - 18
            : isAutoOrientation
              ? RECT_WIDTH / 2 - 4
              : 4,
          rotation: textRotation,
          width: textWidth
        };
        if (labelPlacement === 'outside') {
          if (isRoom) {
            baseLayouts.text = { x: 0, y: 0, rotation: 0 };
          } else {
            baseLayouts.text = {
              x: isAutoOrientation ? 112 : -10,
              y: isLabelOnBottom ? RECT_WIDTH - 16 : isAutoOrientation ? 0 : 2,
              rotation: textRotation,
              width: textWidth
            };
          }
        } else if (labelPlacement === 'outside-node') {
          if (isRoom) {
            baseLayouts.text = { x: 0, y: 0, rotation: 0 };
          } else {
            baseLayouts.text = {
              x: isAutoOrientation ? -50 : -10,
              y: isLabelOnBottom
                ? RECT_WIDTH - 16
                : isAutoOrientation
                  ? RECT_WIDTH / 2 - 4
                  : 2,
              rotation: textRotation,
              width: isAutoOrientation ? textWidth * 2 : textWidth
            };
          }
        } else if (isRoom) {
          baseLayouts.text = { x: 20, y: -10, rotation: 90 };
        }
        break;
      }
      case 'right': {
        const textRotation = isAutoOrientation ? 0 : 0;
        const textWidth = !isAutoOrientation
          ? RECT_HEIGHT - 3
          : RECT_HEIGHT - 26;

        baseLayouts.rect = {
          x: 50,
          y: 0,
          width: RECT_WIDTH,
          height: RECT_HEIGHT,
          rotation: 90
        };
        baseLayouts.circle = { x: 58, y: 45 };
        baseLayouts.text = {
          x: isAutoOrientation ? 0 : 2,
          y: isLabelOnBottom
            ? RECT_WIDTH - 18
            : isAutoOrientation
              ? RECT_WIDTH / 2 - 4
              : 4,
          rotation: textRotation,
          width: textWidth
        };
        if (labelPlacement === 'outside') {
          if (isRoom) {
            baseLayouts.text = { x: 0, y: 0, rotation: 0 };
          } else {
            baseLayouts.text = {
              x: isAutoOrientation ? -10 : RECT_HEIGHT + 4,
              y: isLabelOnBottom ? RECT_WIDTH - 18 : isAutoOrientation ? 0 : 2,
              rotation: textRotation,
              width: textWidth
            };
          }
        } else if (labelPlacement === 'outside-node') {
          if (isRoom) {
            baseLayouts.text = { x: 0, y: 0, rotation: 0 };
          } else {
            baseLayouts.text = {
              x: isAutoOrientation ? 90 : RECT_HEIGHT + 4,
              y: isLabelOnBottom
                ? RECT_WIDTH - 16
                : isAutoOrientation
                  ? RECT_WIDTH / 2 - 4
                  : 2,
              rotation: textRotation,
              width: isAutoOrientation ? textWidth * 2 : textWidth
            };
          }
        } else if (isRoom) {
          baseLayouts.text = { x: 115, y: 130, rotation: -90 };
        }
      }
    }

    const layout = baseLayouts;
    const isVertical =
      baseOrientation === 'left' || baseOrientation === 'right';

    const horizontalOffset = itemIndex * (spacing + (isVertical ? 90 : 100));
    const verticalOffset = rowIndex * (rowSpacing + 90);

    return {
      rect: {
        ...layout.rect,
        x: layout.rect.x + horizontalOffset,
        y: layout.rect.y + verticalOffset,
        width: layout.rect.width,
        height: layout.rect.height,
        rotation: layout.rect.rotation
      },
      circle: {
        x: layout.circle.x + horizontalOffset,
        y: layout.circle.y + verticalOffset
      },
      text: {
        x: layout.text.x + horizontalOffset,
        y: layout.text.y + verticalOffset,
        rotation: layout.text.rotation,
        width: layout.text.width ?? RECT_WIDTH,
        height: layout.text.height ?? RECT_HEIGHT
      }
    };
  }

  if (isRoundRoom) {
    baseLayouts.rect.x = 0;
    baseLayouts.rect.y = 0;
    baseLayouts.rect.width = 80;
    baseLayouts.rect.height = 80;

    baseLayouts.circle.x = 40;
    baseLayouts.circle.y = 40;
    baseLayouts.rect.rotation = 0;

    baseLayouts.text.width = 80;
    baseLayouts.text.y = -30;

    if (isLabelOnBottom) {
      baseLayouts.text.y = 80 + 26;
    }
  } else if (isRoom) {
    baseLayouts.rect.x = 0;
    baseLayouts.rect.y = 0;
    baseLayouts.rect.width = 140;
    baseLayouts.rect.height = 120;

    baseLayouts.circle.x = baseLayouts.rect.width / 2;
    baseLayouts.circle.y = baseLayouts.rect.height / 2;
    baseLayouts.rect.rotation = 0;

    baseLayouts.text.width = baseLayouts.rect.width;
    baseLayouts.text.y = -30;

    if (isLabelOnBottom) {
      baseLayouts.text.y = baseLayouts.rect.height + 26;
    }
  } else if (isParking) {
    // Sized from the spot's own configured width/height (e.g. a grid tool
    // cell) rather than a fixed constant, since a parking rect legitimately
    // varies in size. Falls back to the desk-sized default when unset (an
    // individually-placed parking spot with no explicit size).
    baseLayouts.rect.x = 0;
    baseLayouts.rect.y = 0;
    baseLayouts.rect.width = nodeWidth || RECT_WIDTH;
    baseLayouts.rect.height = nodeHeight || RECT_HEIGHT;

    baseLayouts.circle.x = baseLayouts.rect.width / 2;
    baseLayouts.circle.y = baseLayouts.rect.height / 2;
    baseLayouts.rect.rotation = 0;

    baseLayouts.text.width = baseLayouts.rect.width;
    baseLayouts.text.y = -30;

    if (isLabelOnBottom) {
      baseLayouts.text.y = baseLayouts.rect.height + 26;
    }
  } else if (isSimpleNode) {
    baseLayouts.rect.x = 0;
    baseLayouts.rect.y = 0;
    baseLayouts.circle.x = RECT_WIDTH / 2;
    baseLayouts.circle.y = 0;
    baseLayouts.text.width = RECT_WIDTH;
    baseLayouts.text.x = 0;
    baseLayouts.text.y = 0;

    if (orientation === 'top') {
      baseLayouts.text.y =
        DEFAULT_NODE_CIRCLE_RADIUS + DEFAULT_NODE_CIRCLE_STROKE_WIDTH;
    } else if (orientation === 'bottom') {
      baseLayouts.text.y = -(
        DEFAULT_NODE_CIRCLE_RADIUS +
        DEFAULT_NODE_CIRCLE_STROKE_WIDTH +
        15
      );
    } else if (orientation === 'right') {
      baseLayouts.text.x =
        DEFAULT_NODE_CIRCLE_RADIUS + DEFAULT_NODE_CIRCLE_STROKE_WIDTH + 15;
      baseLayouts.text.y = -5;
      if (isLabelOnBottom) {
        baseLayouts.text.y = 10;
      } else if (isLabelOnTop) {
        baseLayouts.text.y = -DEFAULT_NODE_CIRCLE_RADIUS;
      }
    } else if (orientation === 'left') {
      baseLayouts.text.x = -(
        DEFAULT_NODE_CIRCLE_RADIUS +
        DEFAULT_NODE_CIRCLE_STROKE_WIDTH +
        15
      );
      baseLayouts.text.y = -5;
      if (isLabelOnBottom) {
        baseLayouts.text.y = 10;
      } else if (isLabelOnTop) {
        baseLayouts.text.y = -DEFAULT_NODE_CIRCLE_RADIUS;
      }
    }
  }

  return baseLayouts;
};

type InteractiveZoneProps = Readonly<{
  isSelected: boolean;
  isLastAccessed: boolean;
  /** Walk-up-aware occupancy: for a walk-up, only a still-open (no-end)
   *  check-in counts — never `available_slots` for the whole queried
   *  window. For every other resource type it's the same as the raw
   *  `disabled` flag. Computed once upstream (`hasActiveResourceSchedule`)
   *  so this never has to re-derive walk-up correctness itself. */
  isOccupied: boolean;
  notAssignedResource: boolean;
  notAssignedUser: boolean;
  requiresApproval: boolean;
  isWalkUp?: boolean;
  hasSchedules: boolean;
  showCircleProfilePhoto: boolean;
  hasCheckedIn: boolean;
}>;

export type InteractiveZoneStyles = {
  fill: string;
  stroke: string;
  fillOpacity: number;
  strokeWidth: number;
  /** Konva dash / SVG stroke-dasharray segments (walk-up spots) */
  dash?: number[];
};

export const getInteractiveZoneStyles = ({
  isSelected,
  isLastAccessed,
  isOccupied,
  notAssignedResource,
  notAssignedUser,
  requiresApproval,
  isWalkUp,
  hasSchedules,
  showCircleProfilePhoto,
  hasCheckedIn
}: InteractiveZoneProps): InteractiveZoneStyles => {
  const styles: InteractiveZoneStyles = {
    fill: getCssVariableValue('--map-spot-fully-available-fill'),
    stroke: getCssVariableValue('--map-spot-fully-available-stroke'),
    fillOpacity: 1,
    strokeWidth: DEFAULT_NODE_CIRCLE_STROKE_WIDTH,
    // Walk-up spots stay dashed no matter their occupancy state — it's a
    // resource-type marker, not an availability indicator, so it must
    // survive every branch below rather than only the "available" one.
    dash: isWalkUp ? SPOT_WALK_UP_DASH : undefined
  };

  const hasReservations = hasSchedules;
  const hasAssignedUser = !notAssignedUser;
  // `isOccupied` already carries the walk-up correction (an active, no-end
  // schedule right now — never `available_slots` for the whole queried
  // window), so availability here is a straight negation, not a re-derived
  // `isWalkUp` branch — same correction the click/hover status card applies
  // via getResourceStatus, from the same single upstream computation.
  const isAvailable = !isOccupied;

  if (isWalkUp && isAvailable) {
    styles.fill =
      getCssVariableValue(MAP_SPOT_COLORS.spotWalkUpFill.cssVar) ||
      MAP_SPOT_COLORS.spotWalkUpFill.defaultValue;
    styles.stroke =
      getCssVariableValue(MAP_SPOT_COLORS.spotWalkUpStroke.cssVar) ||
      getCssVariableValue(MAP_SPOT_COLORS.availableColor.cssVar) ||
      MAP_SPOT_COLORS.availableColor.defaultValue;
    return styles;
  }

  //1.1
  if (
    (!isAvailable && !hasReservations && !hasAssignedUser) ||
    notAssignedResource
  ) {
    styles.fill = getCssVariableValue(
      '--map-spot-not-bookeable-unavailable-fill'
    );
    styles.stroke = getCssVariableValue(
      '--map-spot-not-bookeable-unavailable-stroke'
    );
    styles.fillOpacity = 1;

    return styles;
  }

  //1.2 & 1.3
  if (!isAvailable && !hasReservations && hasAssignedUser) {
    // Requires approval
    if (requiresApproval) {
      styles.fill = getCssVariableValue('--map-spot-requires-approval-fill');
      styles.stroke = getCssVariableValue(
        '--map-spot-requires-approval-stroke'
      );
      return styles;
    }

    styles.fill = getCssVariableValue(
      '--map-spot-not-bookeable-unavailable-fill'
    );
    styles.stroke = getCssVariableValue(
      '--map-spot-not-bookeable-unavailable-stroke'
    );

    return styles;
  }

  //2.1
  if (!isAvailable && hasReservations && !hasCheckedIn) {
    // Transparent only when an avatar covers the dot; otherwise (meeting
    // rooms, multi-slot resources) keep the legacy occupied look — grey
    // fill with the reddish ring.
    if (showCircleProfilePhoto) {
      styles.fill = 'transparent';
      return styles;
    }
    styles.fill = getCssVariableValue(
      '--map-spot-not-bookeable-unavailable-fill'
    );
    styles.stroke = getCssVariableValue(
      '--map-spot-not-bookeable-unavailable-stroke'
    );
    return styles;
  }

  //2.2
  if (!isAvailable && hasReservations && hasCheckedIn) {
    if (showCircleProfilePhoto) {
      styles.fill = 'transparent';
      styles.stroke =
        getCssVariableValue(MAP_SPOT_COLORS.spotCheckedInStroke.cssVar) ||
        MAP_SPOT_COLORS.spotCheckedInStroke.defaultValue;
      return styles;
    }
    styles.fill = getCssVariableValue(
      '--map-spot-not-bookeable-unavailable-fill'
    );
    styles.stroke = getCssVariableValue(
      '--map-spot-not-bookeable-unavailable-stroke'
    );
    return styles;
  }

  //3.1
  if (isAvailable && !hasReservations && !hasAssignedUser) {
  }

  //3.2
  if (isAvailable && !hasReservations && hasAssignedUser) {
    styles.fill = getCssVariableValue('--map-spot-requires-approval-fill');
    styles.stroke = getCssVariableValue('--map-spot-requires-approval-stroke');
    return styles;
  }

  // Selected
  if (isSelected) {
    styles.stroke = getCssVariableValue('--map-spot-selected-stroke');
    styles.strokeWidth = 5;
  }

  // Last Accessed
  if (isLastAccessed) {
    styles.fill = getCssVariableValue('--map-spot-fully-available-fill');
  }

  // Requires approval
  if (requiresApproval) {
    styles.fill = getCssVariableValue('--map-spot-requires-approval-fill');
    styles.stroke = getCssVariableValue('--map-spot-requires-approval-stroke');
  }

  // Show Circle Profile Photo (Applies stroke width)
  if (showCircleProfilePhoto) {
    styles.strokeWidth = 7;
  }

  return styles;
};
